import { useCallback, useMemo } from 'react';
import { useGqlQuery } from '@API/useAvQuery';
import { AccountEntities, AccountIngressEntities, DescriptorType } from '@fabric-ui/components';
import { capitalizeSentence, QueryKey } from '@fabric-ui/utils';
import { GENERAL_ENTITY } from '../views/Sources/Mapping/constants';
import { FieldType } from '../views/Sources/Mapping/types';
import { FIELD_VISIBILITY, GET_ENTITIES } from './gql';
import { FieldNameDescriptor } from './utils';

// TODO: remove this when BE will return these fields
const systemFieldsToHideOnBulkUpdate = [
  'current_status.timestamp',
  'integration_info.type',
  'integration_info.key',
  'integration_info.display_key',
  'integration_info.notification_target_id',
  'exception_integration_info.key',
  'exception_integration_info.type',
  'exception_integration_info.display_key',
  'exception_integration_info.notification_target_id',
];

const removeSPFFromSelfPath = (projId, sourceProj, usages) => (projId.name === sourceProj.name ? usages.filter(u => u !== 'SPF') : usages);
const addEVALToPathExcludingAggregatedIngress = (aggsMap, sourceProj, projId) =>
  aggsMap[sourceProj.name].name === projId.name && !aggsMap[projId.name] ? [] : ['EVAL'];

const getUsagesWithEval = ({ projId, usages }, sourceProj, aggsMap) =>
  usages.includes('SPF')
    ? [...removeSPFFromSelfPath(projId, sourceProj, usages), ...addEVALToPathExcludingAggregatedIngress(aggsMap, sourceProj, projId)]
    : usages;

const flatNestedList = fields =>
  fields.flatMap(field =>
    field.type.fields && !field.repeated
      ? flatNestedList(
          field.type.fields.map(f => ({
            ...f,
            displayName: `${field.displayName || ''} ${f.displayName}`.trim(),
            name: `${field.name}.${f.name}`,
          }))
        )
      : [field]
  );

const createExternalProjectionEntityTypeSet = data => new Set(data.externalProjections.map(({ meta }) => meta.typeId.name));

export const createFieldMap = (projs: AccountEntities) =>
  Object.values(projs).reduce(
    (map, { fieldList }) => ({
      ...map,
      ...Object.values(fieldList).reduce(
        (bigMap, list) => ({
          ...bigMap,
          ...list.reduce((obj, field) => ({ ...obj, [field.value]: field }), {}),
        }),
        {}
      ),
    }),
    {}
  );

export const createFieldMapType = (projs: AccountEntities) =>
  Object.values(projs).reduce((map, { typeMap }) => ({ ...map, ...typeMap }), {});

export const getType = (type, repeated) => {
  const result = type.name?.includes('Timestamp')
    ? FieldType.DateTime
    : type.fields
      ? 'MESSAGE'
      : type.kind === 'TYPE_ENUM'
        ? DescriptorType.TYPE_STRING // TODO temporary until it's fully supported AVA-12425
        : DescriptorType[type.kind] || FieldNameDescriptor[type.name] || type.name;
  return repeated ? [result] : result;
};

export const processEntities = ({ data, fieldVisibilityData }) => {
  const externalProjectionEntityTypes = createExternalProjectionEntityTypeSet(data);
  const isExternalProjection = proj => externalProjectionEntityTypes.has(proj.entityTypeId.name);
  const entityTypes = data.allEntityTypes.reduce(
    (obj, { id: { name, builtIn }, keyField, fields }) => ({
      ...obj,
      [name]: {
        fields: [
          ...data.metadataFields.map(item => ({ ...item, isMetadata: true })),
          ...flatNestedList(fields).map(field =>
            systemFieldsToHideOnBulkUpdate.includes(field.name)
              ? { ...field, additionalInfo: { ...field.additionalInfo, isSystemField: true } }
              : field
          ),
        ],
        name,
        keyField,
        builtIn,
      },
    }),
    {}
  );

  // eslint-disable-next-line compat/compat
  const oldIngressProjs = [entityTypes[GENERAL_ENTITY], ...data.ingressProjections]
    .toSorted((a, b) => a.meta?.typeId.name.localeCompare(b.meta?.typeId.name))
    .reduce((obj, projection) => {
      const { builtIn, fields, name, keyField } = entityTypes[projection.meta?.typeId.name || GENERAL_ENTITY];
      return {
        ...obj,
        [name]: {
          name,
          builtIn,
          keyField,
          entityTypeId: projection.meta?.typeId,
          projId: projection.meta?.id,
          projDisplayName: name,
          nameMap: fields.reduce((obj, { name, displayName }) => ({ ...obj, [name]: capitalizeSentence(displayName) }), {}),
          fields: fields.map(f => ({ ...f, displayName: capitalizeSentence(f.displayName) })),
        },
      };
    }, {});

  const getFields = projName => target => {
    const fieldsRecommendationSet = new Set(target.fieldsRecommendation);
    const fieldsPopulatedSet = new Set(target.fieldsPopulation);

    return entityTypes[target.entityTypeId.name].fields.map(field => {
      const fieldVisibilityConfig = fieldVisibilityData.find(dto => {
        const fieldId = `${dto.projectionName}.${dto.fieldName}`;
        return fieldId === `${target.projId.name}.${field.name}`;
      });

      return {
        ...field,
        id: `${projName === target.projId.name ? '' : `${projName}.`}${target.alias}.${field.name}`,
        value: `${target.alias}.${field.name}`,
        ingressValue: `${target.alias}.sources.${field.name}`,
        title: `${target.displayName} ${field.displayName}`,
        group: target.displayName,
        usages: target.usages,
        type: getType(field.type, field.repeated),
        originalType: field.type,
        entityTypeId: target.entityTypeId,
        mainProjId: target.projId,
        visibilityConfig: fieldVisibilityConfig || { config: { hidden: false } },
        isRecommended: fieldsRecommendationSet.has(field.name),
        isPopulated: fieldsPopulatedSet.has(field.name),
      };
    });
  };

  const { ingressProjs, aggProjs, externalProjs } = data.projectionAliasPaths.reduce(
    (acc, proj) => {
      if (isExternalProjection(proj)) {
        acc.externalProjs.push(proj);
        return acc;
      }
      return proj.aggregates ? { ...acc, aggProjs: [...acc.aggProjs, proj] } : { ...acc, ingressProjs: [...acc.ingressProjs, proj] };
    },
    { ingressProjs: [], aggProjs: [], externalProjs: [] }
  );

  const aggsMap = data.projectionAliasPaths.reduce((obj, { projId, aggregates }) => ({ ...obj, [projId.name]: aggregates }), {});

  const parseProj = projs =>
    projs
      .toSorted((a, b) => a.projDisplayName.localeCompare(b.projDisplayName))
      .reduce((obj, { projId, entityTypeId, projDisplayName, aliases: originalAliases, aggregates }) => {
        const aliases =
          aggregates === null
            ? originalAliases
            : originalAliases.map(item => ({ ...item, usages: getUsagesWithEval(item, projId, aggsMap) }));
        const fields = aliases.flatMap(getFields(projId.name));
        const selfTarget = aliases.find(({ projId: { name } }) => name === projId.name);

        return {
          ...obj,
          [projId.name]: {
            projId,
            projDisplayName,
            entityTypeId,
            aggregates: isExternalProjection({ entityTypeId }) ? { name: null, builtIn: null } : aggregates,
            pathAlias: selfTarget.alias,
            fields: getFields(projId.name)(selfTarget),
            nameMap: fields.reduce((obj, { name, displayName }) => ({ ...obj, [name]: displayName }), {}),
            typeMap: fields.reduce((obj, { value, type }) => ({ ...obj, [value]: type }), {}),
            fieldList: fields.reduce(
              (usagesObj, { usages, ...field }) => ({
                ...usagesObj,
                ...usages.reduce((obj, usage) => ({ ...obj, [usage]: [...(usagesObj[usage] || []), field] }), {}),
              }),
              {}
            ),
          },
        };
      }, {}) as AccountEntities;

  const parsedAggProjs = parseProj(aggProjs);
  const parsedIngressProjs = parseProj(ingressProjs) as unknown as AccountIngressEntities;
  const parsedExternalProjs = parseProj(externalProjs);
  const getFieldIdFromProjAndName = (projName, fieldSystemName) => `${parsedAggProjs[projName].pathAlias}.${fieldSystemName}`;
  return {
    aggProjs: parsedAggProjs,
    ingressProjs: parsedIngressProjs,
    externalProjs: parsedExternalProjs,
    oldIngressProjs,
    fieldTypeMap: createFieldMapType(parsedAggProjs),
    fieldMap: createFieldMap(parsedAggProjs),
    getFieldIdFromProjAndName,
  };
};

export default function useEntities(options) {
  const {
    data: entitiesData,
    isPending: entitiesIsLoading,
    refetch: entitiesRefetch,
    error: entitiesError,
  } = useGqlQuery({
    queryKey: [QueryKey.Entities],
    query: GET_ENTITIES,
    select: (_, data) => data,
    ...options,
  });
  const {
    data: fieldVisibilityData,
    isPending: fieldVisibilityIsLoading,
    refetch: fieldVisibilityRefetch,
    error: fieldVisibilityError,
  } = useGqlQuery({
    queryKey: [QueryKey.FieldVisibility],
    query: FIELD_VISIBILITY,
    ...options,
  });

  const data = useMemo(() => {
    if (entitiesData && fieldVisibilityData) {
      return processEntities({ data: entitiesData, fieldVisibilityData });
    }
    return {
      aggProjs: {},
      ingressProjs: {},
      oldIngressProjs: {},
      fieldTypeMap: {},
      fieldMap: {},
      externalProjs: {},
      getFieldIdFromProjAndName: () => '',
    };
  }, [entitiesData, fieldVisibilityData]);

  const isLoading = entitiesIsLoading || fieldVisibilityIsLoading;
  const refetch = useCallback(() => Promise.all([entitiesRefetch(), fieldVisibilityRefetch()]), [entitiesRefetch, fieldVisibilityRefetch]);
  const dataWithMeta = useMemo(() => ({ ...data, isLoading, refetch }), [data, isLoading, refetch]);

  return {
    data: dataWithMeta,
    isLoading,
    error: entitiesError || fieldVisibilityError,
  };
}
